<template>
  <li
    data-qa="item"
    data-index="index"
    :data-orderable="draggable"
    :style="style"
    :class="containerClassName"
  >
    <div :class="$style.controls">
      <span
        v-if="draggable"
        :class="dragHandlerClassName"
      >☰</span>
      <div :class="$style.field">
        <base-input
          :value="value"
          :disabled="disabled"
          :invalid="invalid"
          :class="$style.input"
          data-qa="input"
          size="full"
          :name="name"
          :focus="isNew"
          @input="emit('input', $event)"
        />
        <base-icon
          v-if="!removable"
          :class="$style.lock"
          type="lock"
          :width="8"
          :height="10"
        />
        <slot
          v-if="removable"
          name="remove"
        >
          <remove-button @click="emit('remove', id)" />
        </slot>
      </div>
    </div>
    <slot name="footer" />
  </li>
</template>

<script setup lang="ts">
import { computed, inject, useCssModule } from 'vue';
import BaseIcon from '@/shared/legacy/ui/base-icon/icon.vue';
import BaseInput from '@/shared/legacy/ui/input/input.vue';
import RemoveButton from './remove-button.vue';

type Props = {
  id: number | string;
  name?: string;
  value?: string;
  disabled?: boolean;
  invalid?: boolean;
  isNew?: boolean;
  index: number;
  draggable?: boolean;
  removable?: boolean;
};

const {
  draggable = true,
  removable = true,
  disabled = false,
  invalid = false,
  isNew = false
} = defineProps<Props>();

const emit = defineEmits<{
  input: [value: Event];
  remove: [id: Props['id']];
}>();

const draggableDragHandlerClass = inject('$draggableDragHandlerClass') as string;
const draggableFilterClass = inject('$draggableFilterClass') as string;

const style = useCssModule();

const containerClassName = computed(() => ({
  [style.container]: true,
  [style.removable]: removable,
  [style.draggable]: draggable,
  [draggableFilterClass]: !draggable
}));

const dragHandlerClassName = computed(() => ({
  [style.dragHandler]: true,
  [draggableDragHandlerClass]: true
}));
</script>

<style module>
.controls {
  display: flex;
  align-items: center;
}

.dragHandler {
  display: inline-block;
  flex: none;
  margin-right: calc(-1*var(--draghandler-width) - var(--draghandler-margin));
  margin-left: var(--draghandler-margin);
  width: var(--draghandler-width);
  overflow: hidden;
  text-align: center;
  z-index: 1;
  color: #ccc;
  cursor: pointer;
  user-select: none;
}

.dragHandlerHidden {
  pointer-events: none;
  visibility: hidden;
}

.field {
  flex: 1;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.container.draggable .input {
  padding-left: calc(var(--draghandler-margin) * 2 + var(--draghandler-width));
}

.container:not(.removable) .field {
  padding-right: var(--removebutton-width);
}

.lock {
  width: var(--lockicon-width);
  margin-left: calc(-1 * var(--lockicon-width));
  flex: none;
  pointer-events: none;
  padding: 10px;
  height: 30px;
}
</style>

<i18n lang="json">{}</i18n>
